import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import {
  bool as boolType,
  extend,
  hoist,
  params,
  QueryContext,
  QueryProvider,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCardsDesktop } from '@thd-olt-component-react/category-cards';
import { EtchWidgets } from '@thd-olt-component-react/etch-widgets';
import { Col, Row } from '@thd-olt-component-react/grid';
import { HdhomeCatalogPromo } from '@thd-olt-component-react/hdhome-catalog-promo';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { RectShape, TextBlock, LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { BrowseSearchMetadata, Metadata } from '@thd-olt-component-react/metadata';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { ProductPodDesktop, ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import {
  parseUrl,
  ProductKPFResults,
  ProductResults,
  ProductResultsContext,
  productResultsDataModel,
  ResultsApplied,
  ResultsDimensions,
  ResultsFilterAndSort,
  ResultsHeader,
  ResultsPagination,
  ResultsSortBy,
  ResultsWrapped,
  ShoppableKPF,
  updateQueryParams,
  useProductResults
} from '@thd-olt-component-react/product-results';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { useHistory, useLocation } from '@thd-olt-component-react/router';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { VisualCategoryNavigation } from '@thd-olt-component-react/visual-category-navigation';
import { DynamicBroadIntentAisle } from '@thd-olt-component-react/dynamic-broad-intent-aisle';
import {
  SeoGeneratedContent,
  SeoGeneratedContentLoad,
  SeoGeneratedContentModel,
  SeoLinks,
  SeoLinksLoad,
  SeoLinksModel
} from './dynamic-imports';
import { hasInStoreFilter } from '../../utils';
import { Error } from '../Error';
import '../../styles/browse-search.scss';

const HoistedProductPodGroup = hoist(ProductPodGroup, ProductPodDesktop, CategoryCardsDesktop, CustomProductPodDetails);
// Bizz req to hide shoppable kpf for now
const SHOW_SHOPPABLE_KPF = false;

export const BrowseAndSearch = (props) => {
  declareContexts([ExperienceContext, QueryContext, ProductResultsContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const { pageType } = props;
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const [persistedPageType, setPersistedPageType] = useState();
  const [isErrorStatus, setisErrorStatus] = useState(false);
  const history = useHistory();
  const {
    channel, clientStore, cookie, isDebugMode, segment, deliveryZip: serverDeliveryZip
  } = useContext(ExperienceContext);
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;

  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const storeId = useStoreId({ varnish });
  const isNoResultsFoundRef = useRef(false);
  const SERVER_SIDE_PRODUCT_POD_COUNT = 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 48;
  const searchNavPropsRef = useRef({
    askForSponsored: false,
    deliveryZip: serverDeliveryZip,
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT
  });

  const boldCategories = [
    'area-rugs',
    'interior-lighting',
    'exterior-lighting',
    'ceiling-fans',
    'patio',
    'bathroom-faucets',
    'bathroom-vanities',
    'kitchen-faucets'
  ];

  const isBOLDCategory = boldCategories.indexOf(segment) > -1;
  let podIndex = -1;

  if (clientStore) {
    let deliveryZipCode;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: 48,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode
    };
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true
    };
  }

  const { isClientResolved } = useContext(QueryContext);

  const renderPlaceholder = (browseScheme = '', searchScheme = '', defer = true) => {
    const schemeName = isBrowse ? browseScheme : searchScheme;

    if (!schemeName) return null;

    const isBanner = [
      'cat_sponsored__banner',
      'browse_sponsored__banner'
    ].indexOf(browseScheme) > -1
      || searchScheme === 'search_sponsored__banner';

    let lgName = `recs:${schemeName}`;
    if (schemeName.indexOf('sponsored') > -1) {
      if (isBanner) {
        lgName = 'sponsored-banner';
      } else {
        lgName = 'sponsored-carousel';
      }
    }

    const gridClass = classNames({ 'grid--card grid-space-top': !isBanner });
    return (
      <LayoutGroup name={`${lgName}`}>
        <Row>
          <Col nopadding>
            <div className={gridClass}>
              <RecommendationsPlaceholder
                defer={defer}
                scheme={schemeName}
              />
            </div>
          </Col>
        </Row>
      </LayoutGroup>
    );
  };

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer
        .localize({ storeId: updatedStoreId })
        .then(() => {
          window.location.reload();
        });
    }
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
    setPersistedPageType(pageType);
  }, []);

  const onDimensionsChange = useCallback(({ refinement }) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    history.push(refinement.url);
  });

  const onMultiStoreSelectionChange = useCallback(({ multiStoreIds }) => {
    const updatedPath = updateQueryParams(path, [
      {
        paramName: 'storeSelection',
        paramValue: multiStoreIds
      }
    ]);
    history.push(updatedPath);
  });

  const resultsDimensionsRef = useRef(null);

  const onPageChange = useCallback(({ url }) => {
    history.push(url);

    const { offsetTop } = resultsDimensionsRef?.current?.parentElement?.parentElement;
    window.scrollTo(0, offsetTop || 0);
  });

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page, updatedPath = null
  }) => {
    const newPath = updateQueryParams(updatedPath || path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);
    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    history.push(newPath);
  });

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const isEtchVisNavOnPLP = (data) => {
    try {
      const stringifiedPartialTemplates = data?.searchModel?.partialTemplates;
      const etchVisualNavExists = (stringifiedPartialTemplates?.[0] || '').indexOf('visualNavigation') > -1;
      const hasPLPBanner = data?.searchModel?.metadata?.hasPLPBanner;
      const isPLPPage = data?.searchModel?.metadata?.contentType === 'plppage';
      return hasPLPBanner && etchVisualNavExists && isPLPPage;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error parsing etch templates');
      return false;
    }
  };

  const {
    catStyle,
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    storeSelection,
    skipSpellCheck,
    upperbound
  } = parseUrl({ path: cleanPath });

  const loadedStoreId = useRef(storeId);

  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  if (searchNavPropsRef.current?.askForSponsored && hasSponsoredSorts && cookie?.adobeCookie?.MCMID) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }

  let storefilter = 'ALL';
  if (storeSelection) {
    additionalSearchParams.multiStoreIds = [`${storeSelection}`];
    storefilter = 'IN_STORE';
  }

  const showProducts = /showproducts/i.test(catStyle);
  if (showProducts) {
    additionalSearchParams.plp = true;
  }

  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }

  if (searchNavPropsRef.current?.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }

  const response = useProductResults({
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current?.pageSize,
    start: Nao,
    storeId,
    storefilter,
    sortby,
    sortorder,
    upperbound
  });

  const { data, error, loading, variables } = response;

  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');

  if (data && !loading) {
    loadedStoreId.current = variables.storeId;
  }

  const { products = [], dimensions = [], metadata } = data?.searchModel || {};
  const { contentType, stores } = metadata || {};
  const isCategory = contentType === 'categorypage';
  const isInStoreFilterON = hasInStoreFilter(data);
  const isEtchVisNavInPLP = isEtchVisNavOnPLP(data);

  const {
    features = [],
    loading: kpfLoading
  } = ProductKPFResults({
    itemId: products[0]?.itemId,
    storeId,
    channel
  });

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku)
      .join('-');
  };

  const stringifyAppliedRefinements = (hookData) => {
    const appliedDimensions = hookData?.searchModel?.appliedDimensions || [];
    return appliedDimensions.map(({ refinements = [] }) => {
      return refinements.map((refinement) => refinement.refinementKey).join('-');
    }).join('-');
  };

  useEffect(() => {
    if (data && !loading) {
      const { stores: storeInfo, productCount = {} } = data?.searchModel?.metadata || {};

      const receivedAllProducts = storeId !== '8119' && !!storeInfo;
      const hasNoProducts = productCount?.allProducts === '0';
      const shouldTriggerBrowseAndCategoryAnalytics = isCategory
        || (pageType === 'browse' && (receivedAllProducts || hasNoProducts));

      if (shouldTriggerBrowseAndCategoryAnalytics) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', data?.searchModel);
      } else if (pageType === 'search') {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', data?.searchModel);
      }
    }
  }, [stringifyItemIds(data), stores?.storeId, stringifyAppliedRefinements(data)]);

  const resultsColProps = {
    xs: '8',
    sm: '9',
    md: '10',
    lg: '10'
  };

  const getResxFromData = () => {

    const shouldFetchBanner = (() => {
      const hasBrandCrumb = data?.searchModel?.taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
      const isFeaturedProductNode = pathname.indexOf('/b/Featured-Products') !== -1;

      const hasBrandSearch = data?.searchModel?.metadata?.analytics?.semanticTokens
        && data.searchModel.metadata.analytics.semanticTokens.indexOf('br:{') !== -1;

      return !(hasBrandCrumb || hasBrandSearch || isFeaturedProductNode);
    })();
    const categoryID = data?.searchModel?.metadata?.categoryID;

    const itemIds = (products || []).map((product) => {
      return product.identifiers?.itemId;
    });

    const levels = data?.searchModel?.taxonomy?.breadCrumbs?.reduce((acc, cur) => {
      if (!cur.dimensionName) return acc;
      return `${acc}, ${cur.label}`;
    }, '');

    return {
      appId: 'homedepot01',
      carouselOptions: {
        fullScrollSkip: 2
      },
      criteo: {
        version: 'model2'
      },
      dataContext: {
        searchModel: data?.searchModel
      },
      enableEventSchema: true,
      card: true,
      fetchBanner: shouldFetchBanner,
      itemid: categoryID,
      keyword,
      levels,
      maxProducts: 16,
      organicskus: itemIds ? itemIds.toString() : '',
      products: itemIds ? itemIds.toString() : '',
      recsAndSearchDynamicAPIEnabled: true,
      storeId,
    };
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') return skip;
    const isResolved = isClientResolved({ queryName: 'searchModel' });

    if (attributes.product.fulfillment) {
      if (isResolved) return skip;
      return true;
    }
    return skip;
  };

  const pageNumber = (Nao / searchNavPropsRef.current?.pageSize) + 1;
  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;
  isNoResultsFoundRef.current = pageType === 'search'
    && data?.searchModel?.searchReport?.totalProducts === 0
    && !!keyword;

  const maxNumOfPodsToHydrate = 12;

  const productsReduce = (productPods) => {
    let groupOfPods = 0;

    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (productPods.length === SERVER_SIDE_PRODUCT_POD_COUNT && (!isClientResolved({ queryName: 'searchModel' }))) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT - SERVER_SIDE_PRODUCT_POD_COUNT)
        / maxNumOfPodsToHydrate;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(maxNumOfPodsToHydrate)
          .fill());
      }
    }

    return reduced;
  };

  const originalProducts = useRef(productsReduce(products));
  const [isHydrated, setIsHydrated] = useState(false);
  const hydrators = isHydrated || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;

  if (isErrorStatus) {
    return (
      <Error />
    );
  }

  if (isNoResultsFoundRef.current) {
    return (
      <NoResultsFound
        keyword={keyword}
        recommendationsCtx={getResxFromData()}
      />
    );
  }

  return (
    <RecommendationsProvider resx={getResxFromData()}>
      <BrowseSearchRedirector variables={response?.variables} />
      <Row className="isBound browse-search">
        <LayoutManager
          input={{ isDebugMode }}
          name="browse-and-search-master"
          useContextForProps
        >
          <LayoutGroup name="browse-and-search-metadata">
            <Metadata>
              <BrowseSearchMetadata
                data={data}
                error={error}
                loading={loading}
                pathWithQueryParams={path}
              />
            </Metadata>
          </LayoutGroup>
          <LayoutGroup
            className="u--paddingTop u--paddingBottom"
            name="breadcrumbs"
            tag={Row}
          >
            <Col flatten>
              <Breadcrumbs
                data={data}
                error={error}
                loading={loading}
              />
            </Col>
          </LayoutGroup>
          <LayoutGroup
            name="product-results-group"
            tag={Row}
          >
            <Col flatten>
              <ProductResults
                data={data}
                error={error}
                loading={loading}
              >
                <LayoutManager
                  input={{
                    isDebugMode,
                    isDynamicBrandPage,
                    channel
                  }}
                  name="product-results"
                  useContextForProps
                >
                  {renderPlaceholder(
                    isCategory ? 'cat_sponsored__banner' : 'browse_sponsored__banner',
                    'search_sponsored__banner',
                    false
                  )}
                  <LayoutGroup
                    name="results-header"
                    tag={Row}
                  >
                    <Col flatten>
                      <ResultsHeader
                        resultsRef={resultsDimensionsRef}
                        showShopAllLink={isDynamicBrandPage}
                      />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup
                    name="etch-widgets"
                    tag={Row}
                  >
                    <EtchWidgets
                      data={data}
                      dpdNewTab
                      error={error}
                      exclude={['plpSeo']}
                      keyword={keyword}
                      loading={loading}
                      navParam={nValue}
                      showFavoritesCount
                      storeId={loadedStoreId.current}
                    />
                  </LayoutGroup>
                  {!isEtchVisNavInPLP && (
                    <LayoutGroup
                      name="visual-nav"
                      tag={Row}
                    >
                      <Col className="visual-nav-container">
                        <VisualCategoryNavigation
                          data={data}
                          disableSwipeNavigation
                          loading={loading}
                          showArrows={false}
                          slider
                        />
                      </Col>
                    </LayoutGroup>
                  )}
                  {!isCategory && (
                    <LayoutGroup
                      name="results-applied"
                      tag={Row}
                    >
                      <Col sm="9">
                        <ResultsApplied onAppliedChange={onDimensionsChange} />
                      </Col>
                      <Col xs="3">
                        <ResultsSortBy onSortChange={onSortChange} />
                      </Col>
                    </LayoutGroup>
                  )}
                  {!isCategory && (
                    <LayoutGroup
                      className="browse-search__pod"
                      name="results-dimensions"
                      tag={Row}
                    >
                      <Col
                        lg="2"
                        md="2"
                        sm="3"
                        xs="4"
                      >
                        <Hydrator id="browse-search-dimensions">
                          <ResultsDimensions
                            enableMultiStore
                            enableVisualRefinement
                            multiStoreSelection={storeSelection}
                            onDimensionsChange={onDimensionsChange}
                            onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                            shiftCategoryDimension={isDynamicBrandPage}
                          />
                        </Hydrator>
                      </Col>
                      <Col
                        className="browse-search__pod-col-no-padding"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...resultsColProps}
                      >
                        <LayoutManager
                          input={{
                            isDebugMode,
                            isDynamicBrandPage,
                            channel
                          }}
                          name="product-results-inner"
                          useContextForProps
                        >
                          {isDynamicBrandPage && (
                            <LayoutGroup name="dynamic-aisles">
                              <DynamicBroadIntentAisle
                                searchResponse={response}
                                storeId={loadedStoreId.current}
                              />
                            </LayoutGroup>
                          )}
                          <LayoutGroup
                            name="results-applied"
                            tag={Row}
                          >
                            <Col flatten>
                              <Col sm="9">
                                <ResultsApplied onAppliedChange={onDimensionsChange} />
                              </Col>
                              <Col xs="3">
                                <ResultsSortBy onSortChange={onSortChange} />
                              </Col>
                            </Col>
                          </LayoutGroup>
                          <LayoutGroup name="wrapped-results">
                            <ResultsWrapped>
                              <div
                                className="grid"
                                ref={resultsDimensionsRef}
                              >
                                {!(products || []).length && loading && pageType === 'search' && (
                                  <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />
                                )}
                                {!!hydrators?.length && (
                                  <QueryProvider
                                    cacheKey="search-product-pods"
                                    dataSource="searchNav"
                                    defaultVariables={{ storeId }}
                                    skip={skipFn}
                                  >
                                    {hydrators?.map((group, hydratorIndex) => {
                                      if (!group?.length) return null;
                                      return (
                                        <Hydrator
                                          afterHydrate={() => {
                                            setIsHydrated(true);
                                          }}
                                          className="grid"
                                          id={`browse-search-pods-${hydratorIndex + 1}`}
                                          key={`hydrator-${hydratorIndex}`}
                                          patch={!hydratorIndex}
                                          scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                        >
                                          <HoistedProductPodGroup
                                            DetailsPod={CustomProductPodDetails}
                                            keyword={keyword}
                                          >
                                            {group.map((product, itemIndex, productArray) => {
                                              const { itemId } = product?.identifiers || {};
                                              podIndex += 1;
                                              if (!itemId) {
                                                return (
                                                  <Col
                                                    className="placeholder-product-pod"
                                                    key={`placeholder-${itemIndex}`}
                                                    lg="3"
                                                    md="3"
                                                    sm="3"
                                                    xs="4"
                                                  >
                                                    <RectShape
                                                      color="#E0E0E0"
                                                      style={{
                                                        width: '100%',
                                                        height: '250px',
                                                        marginBottom: '10px'
                                                      }}
                                                    />
                                                    <TextBlock
                                                      color="#E0E0E0"
                                                      rows={7}
                                                    />
                                                  </Col>
                                                );
                                              }
                                              const showContentCard = (
                                                (hydratorIndex * maxNumOfPodsToHydrate)
                                                + itemIndex === SERVER_SIDE_PRODUCT_POD_COUNT);
                                              const podCount = productArray?.length;
                                              return (
                                                <LayoutGroup
                                                  id={`plp-product-pod__${itemIndex}`}
                                                  index={itemIndex}
                                                  key={`${itemId}-${itemIndex}`}
                                                  name={`Product Pod: ${itemIndex}`}
                                                >
                                                  {showContentCard
                                                  && (
                                                    <CategoryCardsDesktop
                                                      data={data}
                                                      error={error}
                                                      loading={loading}
                                                      storeId={loadedStoreId.current}
                                                    />
                                                  )}
                                                  <Col
                                                    className="browse-search__pod"
                                                    fallback="6"
                                                    lg="3"
                                                    md="3"
                                                    sm="4"
                                                    xs="6"
                                                  >
                                                    <ProductPodDesktop
                                                      hover
                                                      isBOLDCategory={isBOLDCategory}
                                                      itemId={itemId}
                                                      mobilePodType="doubleColumn"
                                                      pageNumber={pageNumber}
                                                      podCount={podCount}
                                                      position={itemIndex}
                                                      showFavoritesCount
                                                      storeId={loadedStoreId.current}
                                                      hasInStoreFilter={isInStoreFilterON}
                                                      target="_blank"
                                                    />
                                                  </Col>
                                                  {SHOW_SHOPPABLE_KPF
                                                  && !kpfLoading
                                                  && !!features.length
                                                  && (
                                                    <ShoppableKPF
                                                      dimensions={dimensions}
                                                      features={features}
                                                      itemIndex={podIndex}
                                                    />
                                                  )}
                                                </LayoutGroup>
                                              );
                                            })}
                                          </HoistedProductPodGroup>
                                        </Hydrator>
                                      );
                                    })}
                                  </QueryProvider>
                                )}
                              </div>
                            </ResultsWrapped>
                          </LayoutGroup>
                          <DynamicComponent
                            pageType={pageType}
                          >
                            <Hydrator id="category-cards">
                              <LayoutGroup>
                                {(products?.length < SERVER_SIDE_PRODUCT_POD_COUNT) && (
                                  <CategoryCardsDesktop
                                    data={data}
                                    error={error}
                                    loading={loading}
                                  />
                                )}
                              </LayoutGroup>
                            </Hydrator>
                            <LayoutGroup
                              className="u--paddingTop u--paddingBottom"
                              name="pagination"
                              tag={Row}
                            >
                              <Col>
                                <ResultsPagination
                                  onPageChange={onPageChange}
                                  pageSize={searchNavPropsRef.current?.pageSize}
                                  path={path}
                                />
                              </Col>
                            </LayoutGroup>
                            <Hydrator id="feedback">
                              <LayoutGroup
                                className="u--paddingTop u--paddingBottom"
                                name="search-feedback"
                                tag={Row}
                              >
                                <Col>
                                  <SearchFeedback />
                                </Col>
                              </LayoutGroup>
                            </Hydrator>
                          </DynamicComponent>
                        </LayoutManager>
                      </Col>
                    </LayoutGroup>
                  )}
                </LayoutManager>
              </ProductResults>
            </Col>
          </LayoutGroup>
          <DynamicComponent
            pageType={pageType}
          >
            <Hydrator id="email-promo" className="grid">
              <LayoutGroup
                className="u--paddingTop u--paddingBottom"
                name="email-promo"
                tag={Row}
              >
                <Col>
                  <HdhomeCatalogPromo formSubmission />
                </Col>
              </LayoutGroup>
            </Hydrator>
          </DynamicComponent>
          {renderPlaceholder(
            isCategory ? 'cat_sponsored' : 'browse_sponsored',
            'search_sponsored',
            false
          )}
          {renderPlaceholder(
            'PLP_Browse',
            'searchViewed'
          )}
          {renderPlaceholder(
            '',
            'plpsearch_multi'
          )}
          {renderPlaceholder(
            isCategory ? 'rv_categorypages_rr' : 'rv_nav_plp_rr',
            'rv_search_plp_rr'
          )}
          <DynamicComponent
            pageType={pageType}
          >
            <Hydrator
              className="grid"
              id="search-seo-section"
              delay={500}
              placeholder={(
                <LoadingPlaceholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 750 }}
                  type="rect"
                >
                  <div />
                </LoadingPlaceholder>
              )}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
            >
              <LayoutGroup
                className="u--paddingTop u--paddingBottom"
                name="related-search"
                tag={Row}
              >
                <Col
                  nopadding
                  // eslint-disable-next-line react/jsx-boolean-value
                  suppressHydrationWarning={true}
                >
                  <RelatedSearch
                    navParam={nValue}
                    searchData={data}
                    searchError={error}
                    searchLoading={loading}
                    storeId={loadedStoreId.current}
                  />
                </Col>
              </LayoutGroup>
            </Hydrator>
          </DynamicComponent>
          {pageType !== 'search' && (
            <DynamicComponent
              pageType={pageType}
            >
              <Hydrator
                className="grid"
                id="browse-seo-section"
                delay={1500}
                placeholder={(
                  <LoadingPlaceholder
                    color="#f5f5f5"
                    ready={false}
                    style={{ height: 750 }}
                    type="rect"
                  >
                    <div />
                  </LoadingPlaceholder>
                )}
                waitFor={[SeoGeneratedContentLoad, SeoLinksLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <LayoutGroup
                  name="etch-seo"
                  tag={Row}
                >
                  <Col>
                    <EtchWidgets
                      data={data}
                      error={error}
                      include={['plpSeo']}
                      loading={loading}
                    />
                  </Col>
                </LayoutGroup>
                <LayoutGroup
                  className="u--paddingTop u--paddingBottom"
                  name="email-promo"
                  tag={Row}
                >
                  <Col>
                    <SeoGeneratedContent nvalue={`N-${nValue}`} />
                  </Col>
                </LayoutGroup>
                <LayoutGroup
                  className="u--paddingTop u--paddingBottom"
                  name="seo-mmg"
                  tag={Row}
                >
                  <Col nopadding>
                    <SeoLinks path={cleanedPath} />
                  </Col>
                </LayoutGroup>
              </Hydrator>
            </DynamicComponent>
          )}
        </LayoutManager>
        <BackToTop />
      </Row>
    </RecommendationsProvider>
  );
};

BrowseAndSearch.propTypes = {
  pageType: string.isRequired
};

BrowseAndSearch.defaultProps = {};

BrowseAndSearch.dataModel = extend({
  searchModel: params()
    .shape({
      metadata: shapeType({
        hasPLPBanner: boolType(),
        categoryID: stringType(),
        analytics: shapeType({
          semanticTokens: stringType(),
          dynamicLCA: stringType()
        })
      })
    })
},
ResultsDimensions,
ResultsFilterAndSort,
BrowseSearchRedirector,
BrowseSearchMetadata,
Breadcrumbs,
EtchWidgets,
VisualCategoryNavigation,
CategoryCardsDesktop,
SeoGeneratedContentModel,
SeoLinksModel,
RelatedSearch,
RecommendationsPlaceholder,
productResultsDataModel,
ProductKPFResults,
DynamicBroadIntentAisle
);
